import React, { useMemo, useState } from 'react';
import styles from './SurveySearchPage.module.css';
import Banner from '../../../components/Banner';

function KeyValuePairs({
  keyValuePairs,
  setKeyValuePairs,
  setQueryParams,
  variablesMapping,
}) {
  const reverseMapping = useMemo(() => {
    const rev = {};
    Object.entries(variablesMapping || {}).forEach(([tKey, obj]) => {
      Object.entries(obj || {}).forEach(([varKey, label]) => {
        if (!rev[label]) {
          rev[label] = `${tKey}${varKey}`;
        } else {
          rev[label] = rev[label] + `|${tKey}${varKey}`;
        }
      });
    });
    return rev;
  }, [variablesMapping]);

  const rawKeys = useMemo(() => {
    const keys = new Set();
    Object.values(variablesMapping || {}).forEach((obj) => {
      Object.keys(obj || {}).forEach((varKey) => keys.add(varKey));
    });
    return Array.from(keys);
  }, [variablesMapping]);

  // Track search term per row
  const [searchTerms, setSearchTerms] = useState(
    Array(keyValuePairs.length).fill('')
  );
  // Track which input is focused
  const [focusedIndex, setFocusedIndex] = useState(null);

  // Banner state
  const [banner, setBanner] = useState({
    open: false,
    message: '',
    severity: 'error',
  });

  // Dropdown options: transformed labels + all raw keys, with searchTerm at the top (italic, quoted)
  const getDropdownOptions = (searchTerm, excludeKeys = []) => {
    const labelOptions = Object.keys(reverseMapping);
    const allOptions = [...labelOptions, ...rawKeys].filter(
      (v, i, arr) => arr.indexOf(v) === i // deduplicate
    );
    // Exclude keys already used in other rows
    const filteredOptions = allOptions.filter(
      (opt) => !excludeKeys.includes(opt)
    );
    let filtered = filteredOptions;
    if (searchTerm) {
      filtered = filteredOptions.filter((label) =>
        label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      // Always show searchTerm as first option, quoted and italic, if not already present
      if (!filtered.includes(searchTerm)) {
        filtered = [searchTerm, ...filtered];
      }
    }
    return filtered;
  };

  // Supported relations
  const relationOptions = [
    { value: '=', label: '=' },
    { value: '!=', label: '≠' },
    { value: '>', label: '>' },
    { value: '<', label: '<' },
  ];

  const handleKeyValuePairChange = (index, field, value, usedKeys = []) => {
    const newPairs = [...keyValuePairs];

    // Only block duplicate key usage if dropdown is not shown (handled onBlur)
    newPairs[index][field] = value;

    // Update searchTerms if key changed
    if (field === 'key') {
      const newSearchTerms = [...searchTerms];
      newSearchTerms[index] = value;
      setSearchTerms(newSearchTerms);
    }

    // Auto-add new row if current row has both key and value
    const currentPair = newPairs[index];
    if (currentPair.key.trim() && currentPair.values.trim()) {
      // Check if this is the last row and it's complete
      const isLastRow = index === newPairs.length - 1;
      if (isLastRow) {
        newPairs.push({ key: '', relation: '=', values: '' });
      }
    }

    // Auto-remove empty rows (but keep at least one)
    const filteredPairs = newPairs.filter((pair, i) => {
      if (newPairs.length === 1) return true;
      if (pair.key.trim() || pair.values.trim()) return true;
      if (i === newPairs.length - 1) return true;
      return false;
    });

    // Ensure we always have at least one row
    const finalPairs =
      filteredPairs.length === 0
        ? [{ key: '', relation: '=', values: '' }]
        : filteredPairs;

    setKeyValuePairs(finalPairs);

    // Build keyValues for queryParams
    // Now: { [key:relation]: values[] }
    const validPairs = finalPairs.reduce((acc, pair) => {
      if (pair.key && pair.values) {
        const valuesArr = pair.values
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v);
        if (valuesArr.length) {
          const rel = pair.relation || '=';
          const keyStr = reverseMapping[pair.key]
            ? reverseMapping[pair.key]
            : pair.key;
          acc[`${keyStr}${rel}`] = valuesArr;
        }
      }
      return acc;
    }, {});
    setQueryParams((prev) => ({ ...prev, keyValues: validPairs }));
  };

  const removeKeyValuePair = (index) => {
    if (keyValuePairs.length > 1) {
      const newPairs = keyValuePairs.filter((_, i) => i !== index);
      setKeyValuePairs(newPairs);
      // Update queryParams
      const validPairs = newPairs.reduce((acc, pair) => {
        if (pair.key && pair.values) {
          const valuesArr = pair.values
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v);
          if (valuesArr.length) {
            const rel = pair.relation || '=';
            const keyStr = reverseMapping[pair.key]
              ? reverseMapping[pair.key]
              : pair.key;
            acc[`${keyStr}${rel}`] = valuesArr;
          }
        }
        return acc;
      }, {});
      setQueryParams((prev) => ({ ...prev, keyValues: validPairs }));
    }
  };

  // Update searchTerms array when rows change
  React.useEffect(() => {
    if (searchTerms.length !== keyValuePairs.length) {
      setSearchTerms(Array(keyValuePairs.length).fill(''));
    }
  }, [keyValuePairs.length]);

  return (
    <div className={`${styles.keyValueBox}`}>
      <Banner
        open={banner.open}
        message={banner.message}
        severity={banner.severity}
        onClose={() => setBanner((prev) => ({ ...prev, open: false }))}
      />
      <label className={styles.label} style={{ gridColumn: '1 / -1' }}>
        Question/Answers:
      </label>
      <div>
        {keyValuePairs.map((pair, index) => {
          // Collect keys used in other rows
          const usedKeys = keyValuePairs
            .map((p, i) => (i !== index ? p.key.trim() : null))
            .filter((k) => k);
          return (
            <div key={index} className={styles.keyValueRow}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '50%',
                }}
              >
                <div style={{ position: 'relative', flex: 1, width: '100%' }}>
                  <input
                    type="text"
                    placeholder="Question label"
                    value={pair.key}
                    onChange={(e) => {
                      handleKeyValuePairChange(
                        index,
                        'key',
                        e.target.value,
                        usedKeys
                      );
                    }}
                    className={styles.input}
                    autoComplete="off"
                    style={{ width: '100%', boxSizing: 'border-box' }}
                    onFocus={() => {
                      setFocusedIndex(index);
                      setSearchTerms((terms) => {
                        const newTerms = [...terms];
                        newTerms[index] = pair.key;
                        return newTerms;
                      });
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setFocusedIndex(null);
                        const val = pair.key.trim();
                        if (val && usedKeys.includes(val)) {
                          const newPairs = [...keyValuePairs];
                          newPairs[index].key = '';
                          setKeyValuePairs(newPairs);
                          setSearchTerms((terms) => {
                            const newTerms = [...terms];
                            newTerms[index] = '';
                            return newTerms;
                          });
                          setBanner({
                            open: true,
                            message: `The question "${val}" is already used in another row.`,
                            severity: 'error',
                          });
                        }
                      }, 100);
                    }}
                  />
                  {/* Dropdown for search */}
                  {focusedIndex === index && searchTerms[index] && (
                    <div
                      style={{
                        position: 'absolute',
                        zIndex: 10,
                        background: '#fff',
                        border: '1px solid #ccc',
                        width: '100%',
                        maxHeight: 200,
                        overflowY: 'auto',
                      }}
                    >
                      {getDropdownOptions(searchTerms[index], usedKeys)
                        .length === 0 && (
                        <div style={{ padding: 8, color: '#888' }}>
                          No matches found
                        </div>
                      )}
                      {getDropdownOptions(searchTerms[index], usedKeys).map(
                        (option, i) => (
                          <div
                            key={option}
                            style={{
                              padding: 8,
                              cursor: 'pointer',
                              background:
                                option === pair.key ? '#f0f0f0' : '#fff',
                              fontStyle:
                                i === 0 &&
                                searchTerms[index] &&
                                option === searchTerms[index]
                                  ? 'italic'
                                  : 'normal',
                            }}
                            onMouseDown={() => {
                              setSearchTerms((terms) => {
                                const newTerms = [...terms];
                                newTerms[index] = '';
                                return newTerms;
                              });
                              setFocusedIndex(null);
                              handleKeyValuePairChange(
                                index,
                                'key',
                                option,
                                usedKeys
                              );
                            }}
                          >
                            {i === 0 &&
                            searchTerms[index] &&
                            option === searchTerms[index]
                              ? `"${option}"`
                              : option}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
                <select
                  value={pair.relation || '='}
                  onChange={(e) =>
                    handleKeyValuePairChange(index, 'relation', e.target.value)
                  }
                  className={styles.input}
                  style={{
                    gridColumn: '2',
                    maxWidth: 60,
                    minWidth: 50,
                    marginRight: 1,
                  }}
                >
                  {relationOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '50%',
                }}
              >
                <input
                  type="text"
                  placeholder="Answer Value (comma-separated for multiple values)"
                  value={pair.values}
                  onChange={(e) =>
                    handleKeyValuePairChange(index, 'values', e.target.value)
                  }
                  className={styles.input}
                  style={{ gridColumn: '3' }}
                />
                {keyValuePairs.length > 1 &&
                  index !== keyValuePairs.length - 1 && (
                    <button
                      onClick={() => removeKeyValuePair(index)}
                      className={styles.removeBtnRed}
                      style={{ gridColumn: '4' }}
                    >
                      ❌
                    </button>
                  )}
              </div>
            </div>
          );
        })}
      </div>
      <small className={styles.helpText} style={{ gridColumn: '1 / -1' }}>
        To enter multiple answer values, separate them by commas. Example:{' '}
        <b>Alter</b> = <i>18,19,20</i>
      </small>
    </div>
  );
}

export default KeyValuePairs;
